import { RouteName } from "constant/routeNames";
import React, { useEffect, useState } from "react";
import { FaCrown, FaRegCheckCircle } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { updateUserRole } from "redux/userRedux";
import PaymentService from "services/PaymentService";
import MiniPackagesCard from "./shared/MiniPackagesCard";
import SubscriptionService from "services/subscription/subscriptionService";
import {
  MiniPackagesResponse,
  subscriptionsResponse,
} from "services/subscription/types";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper";
import "swiper/css";
import "swiper/css/pagination";
import { displayTimeFormat } from "utils/helperFuncs";

const FE_URL = process.env.REACT_APP_FRONTEND_URL;
const PAGE_TITLE = "Subscription Plans - AYOZAT";
const PAGE_DESCRIPTION =
  "Choose the AYOZAT plan that's right for you. Unlimited Live TV, movies, series, music, podcasts and radio - all in one subscription.";
const PAGE_URL = `${FE_URL}/subscription/plan`;

const SubscriptionPlan = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector((state: any) => state?.user?.currentUser);

  const [subscription, setSubscription] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const hasActiveSubscription = subscription?.status === "active";
  const [subscriptionsPackages, setSubscriptionPackages] =
    useState<subscriptionsResponse>();

  // Scroll to top when the page loads
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  //get all mini packages
  const fetchAllMiniPackages = async () => {
    try {
      const resp = await SubscriptionService.getPackages();
      if (resp.status) {
        setSubscriptionPackages(resp.data);
      }
    } catch (err) {
      console.log("err ::::", err);
    }
  };

  const mainPackage = subscriptionsPackages?.main?.[0];
  const plans = mainPackage
    ? [
        {
          id: mainPackage.id,
          priceId: "price_1TZEHVC7WwZI5dSVfI46cLdY", // Valid Stripe price ID
          name: `${mainPackage.title} Plan`,
          price: `$${mainPackage.price}`,
          rawPrice: 9800,
          description: mainPackage.description,
          timeFormat: mainPackage.plan_period_format,
          features: [
            "Monthly plan — cancel anytime",
            "Unlimited Live TV, movies & series",
            "Exclusive Music, Podcasts & Radio",
            "Watch anywhere, any device, in HD",
            "Everything in one plan",
          ],
          buttonText: "Sign up with Pro Plan",
          isPrimary: true,
        },
      ]
    : [];

  // Fetch subscription status on mount
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }
      try {
        const response = await PaymentService.getSubscription();
        const subData = response?.data || response;
        setSubscription(subData);

        // If subscription is active, update user role in Redux
        if (subData?.status === "active") {
          dispatch(updateUserRole("plus_subscription"));
        }
      } catch (error) {
        console.error("Error fetching subscription:", error);
        setSubscription(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [currentUser, dispatch]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  //fetch mini packages data
  useEffect(() => {
    fetchAllMiniPackages();
  }, []);

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
      </Helmet>
      <div
        className={`text-white transition-all duration-300 ease-in-out py-8 sm:py-10 px-4 sm:px-6 lg:px-8 relative overflow-hidden ${plans.length ? "min-h-[100vh]" : "h-full"}`}
      >
      {/* Spinning gradient border keyframes */}
      <style>{`
        @keyframes spin-border {
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
        .spin-border-glow {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 150vmax;
          height: 150vmax;
          transform: translate(-50%, -50%);
          background: conic-gradient(
            from 0deg,
            transparent 0deg,
            #ffffff 40deg,
            var(--red, #f30543) 90deg,
            #ff8a8a 140deg,
            transparent 200deg,
            transparent 360deg
          );
          animation: spin-border 3s linear infinite;
          animation-play-state: paused;
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
        }
        .group:hover .spin-border-glow {
          opacity: 1;
          animation-play-state: running;
        }
      `}</style>

      {/* Background radial glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] max-w-full h-[250px] bg-[var(--red)] opacity-[0.06] blur-[100px] pointer-events-none rounded-full"></div>

      <div className="relative z-10 max-w-5xl pt-2 mx-auto sm:pt-4">
        <div className="mb-8 text-center sm:mb-10">
          <h2 className="text-3xl sm:text-4xl md:text-[42px] font-medium tracking-tight text-[#fdfdfd] leading-tight">
            Simple and Affordable
            <br />
            Pricing Plans
          </h2>
          <p className="max-w-xl px-2 mx-auto mt-3 text-xs font-light text-gray-400 sm:text-sm">
            Find the perfect plan for your screen time
          </p>
        </div>

        {/* Active Subscription Banner */}
        {!loading && hasActiveSubscription && (
          <div className="hidden w-full max-w-sm mx-auto mb-8 md:max-w-md">
            <div className="relative rounded-2xl p-4 sm:p-5 bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border border-[#2a2a4a] overflow-hidden">
              {/* Glow effect */}
              <div className="absolute top-0 right-0 w-[150px] h-[150px] bg-[var(--red)] opacity-[0.08] blur-[60px] pointer-events-none rounded-full"></div>

              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#e30030] to-[#ff6b6b] flex items-center justify-center shadow-lg shadow-red-500/20 flex-shrink-0">
                  <FaCrown className="text-white text-[16px]" />
                </div>
                <div>
                  <h3 className="text-[16px] font-semibold text-white">
                    Your Current Plan
                  </h3>
                  <p className="text-[12px] text-green-400 font-medium">
                    ● Active
                  </p>
                </div>
              </div>

              <div className="space-y-3 text-[13px]">
                <div className="flex justify-between items-center gap-2 py-2 border-b border-[#ffffff0a]">
                  <span className="text-gray-400">Plan</span>
                  <span className="font-medium text-white">Plus Plan</span>
                </div>
                <div className="flex justify-between items-center gap-2 py-2 border-b border-[#ffffff0a]">
                  <span className="text-gray-400">Status</span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-green-500/10 text-green-400 border border-green-500/20">
                    Active
                  </span>
                </div>
                {subscription?.current_period_start && (
                  <div className="flex justify-between items-center gap-2 py-2 border-b border-[#ffffff0a]">
                    <span className="text-gray-400">Current Period Start</span>
                    <span className="text-right text-gray-300">
                      {formatDate(subscription.current_period_start)}
                    </span>
                  </div>
                )}
                {subscription?.current_period_end && (
                  <div className="flex justify-between items-center gap-2 py-2 border-b border-[#ffffff0a]">
                    <span className="text-gray-400">Next Renewal</span>
                    <span className="text-right text-gray-300">
                      {formatDate(subscription.current_period_end)}
                    </span>
                  </div>
                )}
                {subscription?.cancel_at_period_end && (
                  <div className="flex justify-between items-center gap-2 py-2 border-b border-[#ffffff0a]">
                    <span className="text-gray-400">Cancellation</span>
                    <span className="text-yellow-400 text-[12px] text-right">
                      Cancels at period end
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center w-full max-w-sm py-20 mx-auto md:max-w-md">
            <div className="w-10 h-10 border-4 border-gray-700 rounded-full border-t-red-500 animate-spin"></div>
          </div>
        )}

        {!loading && plans.length > 0 && (
          <div className="flex items-stretch justify-center w-full max-w-sm mx-auto md:max-w-md">
            {plans.map((plan, index) => (
              <div
                key={plan.name}
                className={`w-full relative rounded-3xl p-5 sm:p-6 flex flex-col bg-[#111111] border transition-all duration-300 ease-out ${
                  plan.isPrimary
                    ? "border-[#2a2a2a] shadow-[0_-15px_40px_-15px_rgba(243,5,67,0.15)] shadow-[var(--red)] transform lg:-translate-y-2 hover:-translate-y-1 lg:hover:-translate-y-3 hover:border-[var(--red)]/60 hover:shadow-[0_-20px_55px_-15px_rgba(243,5,67,0.4)]"
                    : "border-[#1c1c1c] hover:-translate-y-1 hover:border-[#2a2a2a] hover:shadow-2xl"
                } overflow-hidden group`}
              >
                {plan.isPrimary && (
                  <div className="absolute -top-[1px] left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[var(--red)] to-transparent opacity-90 mx-6 blur-[1px] transition-all duration-300 group-hover:opacity-100 group-hover:mx-3"></div>
                )}

                <div className="mb-4">
                  <h3 className="text-[17px] text-gray-300 font-normal">
                    {plan.name}
                  </h3>
                  <div className="mt-4 flex items-baseline font-bold text-[var(--red)]">
                    <span className="text-[40px] sm:text-[44px] tracking-tight leading-none">
                      {plan.price}
                    </span>
                    <span className="ml-2 text-xs font-normal text-gray-400">
                      /{displayTimeFormat(plan.timeFormat)}
                    </span>
                  </div>
                  <p className="text-gray-400 mt-4 text-[13px] leading-relaxed pr-2 sm:pr-8 font-light">
                    {plan.description}
                  </p>
                </div>

                <div
                  className={`relative mt-2 mb-6 rounded-xl ${
                    plan.isPrimary && !hasActiveSubscription
                      ? "overflow-hidden p-[2px] shadow-lg shadow-[var(--red)]/30"
                      : ""
                  }`}
                >
                  {/* Spinning gradient border (primary plan only) */}
                  {plan.isPrimary && !hasActiveSubscription && (
                    <span aria-hidden className="spin-border-glow" />
                  )}

                  <button
                    className={`relative z-[1] w-full py-[12px] px-4 text-[14px] font-medium transition-all duration-300 ${
                      hasActiveSubscription
                        ? "rounded-xl bg-[#202020] border border-[#2e2e2e] text-gray-500 cursor-not-allowed opacity-60"
                        : plan.isPrimary
                          ? "rounded-[10px] bg-gradient-to-r from-[#e30030] to-[#c70025] text-white hover:brightness-110"
                          : "rounded-xl bg-[#202020] border border-[#2e2e2e] text-gray-300 hover:bg-[#282828]"
                    }`}
                    disabled={hasActiveSubscription}
                    onClick={() =>
                      navigate(RouteName.SubscriptionPlanCheckout, {
                        state: { plan: plan },
                      })
                    }
                  >
                    {hasActiveSubscription
                      ? "Already Subscribed"
                      : plan.buttonText}
                  </button>
                </div>

                <div className="flex items-center gap-4 mb-6">
                  <div className="h-px bg-[#222] flex-1"></div>
                  <span className="text-[#666] text-[9px] font-semibold tracking-[0.15em]">
                    FEATURES
                  </span>
                  <div className="h-px bg-[#222] flex-1"></div>
                </div>

                <ul className="flex-1 space-y-3">
                  {plan.features.map((feature, featureIdx) => (
                    <li
                      key={featureIdx}
                      className="flex items-center text-[#999] text-[13px] font-light"
                    >
                      <FaRegCheckCircle className="flex-shrink-0 mr-4 text-[#555] text-[15px]" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="w-[92%] sm:w-[90%] mx-auto mt-12 sm:mt-16 flex flex-col gap-6 sm:gap-[32px]">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="font-normal mb-2 text-2xl sm:text-3xl md:text-[36px]">
              Mini Packages
            </p>
            <p className="text-sm font-normal text-gray-300 sm:text-base">
              Customize your lineup with specialized content hubs.
            </p>
          </div>
          <Link
            to={RouteName.seeAllMiniSubscriptionPlanRoute}
            className="shrink-0"
          >
            <p className="font-[500] text-[15px] text-[#F50537] cursor-pointer">
              See all
            </p>
          </Link>
        </div>

        {/* card section */}
        {(subscriptionsPackages?.mini?.length ?? 0) > 6 ? (
          <div className="w-full min-w-0 mt-2 mb-8 sm:mt-4">
            <Swiper
              slidesPerView={6}
              spaceBetween={14}
              style={{
                overflow: "hidden",
                padding: "40px 32px",
                margin: "-20px -12px",
              }}
              breakpoints={{
                0: { slidesPerView: 2, spaceBetween: 10 },
                640: { slidesPerView: 3, spaceBetween: 12 },
                768: { slidesPerView: 4, spaceBetween: 14 },
                1024: { slidesPerView: 5, spaceBetween: 14 },
                1280: { slidesPerView: 6, spaceBetween: 16 },
              }}
              modules={[Pagination, Navigation]}
              navigation={true}
              className="w-full mySwiper"
            >
              {subscriptionsPackages!.mini.map((card: MiniPackagesResponse) => (
                <SwiperSlide key={card?.id}>
                  <MiniPackagesCard card={card} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        ) : (
          <div className="grid w-full grid-cols-2 gap-3 mt-2 mb-8 sm:gap-4 sm:mt-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-6 max-sm:w-full">
            {subscriptionsPackages?.mini.map((card: MiniPackagesResponse) => (
              <div key={card?.id}>
                <MiniPackagesCard card={card} />
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default SubscriptionPlan;
